import { cn } from '@/lib/utils';
import { Loader, X } from 'lucide-react';
import { useState } from 'react';

interface Participant {
  id: string;
  name: string;
}

interface ParticipantSelectionModalProps {
  participants: Participant[];
  onClose: () => void;
  onSelect: (participantId: string) => void;
  isOpen: boolean;
  onAnalyze: (
    participantName: string,
    participants: string[],
  ) => Promise<string>;
}

export default function ParticipantSelectionModal({
  participants,
  onClose,
  onSelect,
  isOpen,
  onAnalyze,
}: ParticipantSelectionModalProps) {
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(
    null,
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="p-6 relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>

          <h2 className="text-xl font-bold mb-4">
            Select participant to analyze
          </h2>

          <p className="text-gray-600 mb-6">
            We've identified these participants in the meeting. Please select
            the participant you want to analyze:
          </p>

          <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
            {participants.map((participant) => (
              <div key={participant.id} className="flex items-center">
                <div className="relative">
                  <input
                    type="radio"
                    id={participant.id}
                    name="participant"
                    value={participant.id}
                    checked={selectedParticipant === participant.id}
                    onChange={() => {
                      setSelectedParticipant(participant.id);
                      setError(null);
                    }}
                    className="appearance-none w-5 h-5 rounded-full border border-gray-300 checked:border-[6px] checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <label
                  htmlFor={participant.id}
                  className="ml-3 block text-sm font-medium text-gray-700"
                >
                  {participant.name}
                </label>
              </div>
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={async () => {
                if (selectedParticipant) {
                  setIsAnalyzing(true);
                  setError(null);
                  try {
                    const selectedName = participants.find(
                      (p) => p.id === selectedParticipant,
                    )?.name;
                    if (!selectedName) {
                      throw new Error('Selected participant not found');
                    }
                    // We need to call onAnalyze with the participant name
                    // It will return the analysis ID which we don't need to use here
                    await onAnalyze(
                      selectedName,
                      participants.map((p) => p.name),
                    );
                    onSelect(selectedParticipant);
                  } catch (err) {
                    console.error('Analysis failed:', err);
                    setError(
                      'Failed to analyze the transcript. Please try again.',
                    );
                    setIsAnalyzing(false);
                  }
                }
              }}
              disabled={!selectedParticipant || isAnalyzing}
              className={cn(
                'rounded-full px-8 py-2 transition-colors font-medium flex items-center',
                selectedParticipant && !isAnalyzing
                  ? 'bg-primary hover:bg-primary/90 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed',
              )}
            >
              {isAnalyzing ? (
                <>
                  <Loader size={16} className="animate-spin mr-2" />
                  Analyzing...
                </>
              ) : (
                'Get insights'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
